import { useMemo, useRef, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import EditButtons from '../../common/editButtons'
import FormikContext from '../../../context/formikContext'
import { EditableContext } from '../../../context/editableContext'
import { useGetPostsTitle } from '../../../hooks/api/postsTitle'
import useMediaQuery from '@mui/material/useMediaQuery'

// Import the extracted components
import IdentityForm from './personalInformationComponents/IdentityForm'
import MainPhoneForm from './personalInformationComponents/MainPhoneForm'
import OtherPhonesForm from './personalInformationComponents/OtherPhonesForm'
import AdressForm from './personalInformationComponents/AdressForm'
import AdditionalInformationForm from './personalInformationComponents/AdditionalInformationForm'

interface Props {
  formik: any
  isEditable: boolean
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>
}

// Mapping des noms de champs vers leurs labels en français
const fieldLabels: Record<string, string> = {
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'Courriel',
  recovery_email: 'Courriel de récupération',
  date_of_birth: 'Date de naissance',
  sin_last_four: 'Numéro d\'assurance sociale (4 derniers chiffres)',
  title: 'Titre',
  titles: 'Titre',
  'mainPhone.phone_number': 'Téléphone principal',
  'mainPhone.type': 'Type de téléphone principal',
  'otherPhones1.phone_number': 'Téléphone secondaire 1',
  'otherPhones1.type': 'Type de téléphone secondaire 1',
  'otherPhones2.phone_number': 'Téléphone secondaire 2',
  'otherPhones2.type': 'Type de téléphone secondaire 2',
  'address.civic_number': 'Numéro civique',
  'address.street': 'Rue',
  'address.city': 'Ville',
  'address.city_id': 'Ville',
  'address.postal_code': 'Code postal',
  'address.province': 'Province',
  'address.office': 'Bureau/Appartement',
}

// Helper function to extract all error messages from formik errors with field labels
const getErrorMessages = (errors: any): string[] => {
  const messages: string[] = []
  
  const extractErrors = (obj: any, prefix = '') => {
    Object.keys(obj).forEach(key => {
      const value = obj[key]
      const fullPath = prefix ? `${prefix}.${key}` : key
      
      if (typeof value === 'string') {
        const fieldLabel = fieldLabels[fullPath] || fieldLabels[key] || key
        messages.push(`${fieldLabel} : ${value}`)
      } else if (typeof value === 'object' && value !== null) {
        extractErrors(value, fullPath)
      }
    })
  }
  
  extractErrors(errors)
  return messages
}

const PersonalInformationsForm = ({ formik, isEditable, setIsEditable }: Props) => {
  const isMobile = useMediaQuery('(max-width:800px)')
  const { data: listTitles } = useGetPostsTitle()
  const errorsRef = useRef<HTMLDivElement>(null)

  const editableState = useMemo(() => ({ isEditable, setIsEditable }), [isEditable, setIsEditable])
  
  // Get error messages when there are validation errors
  const errorMessages = useMemo(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      return getErrorMessages(formik.errors)
    }
    return []
  }, [formik.errors, formik.submitCount])

  // Scroll to errors only when form is submitted (not on every error change)
  useEffect(() => {
    if (formik.submitCount > 0 && errorMessages.length > 0 && errorsRef.current) {
      errorsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [formik.submitCount])

  return (
    <EditableContext.Provider value={editableState}>
      <FormikContext.Provider value={formik}>
        <Box component='form' sx={{ pt: '48px', position: 'relative' }}>
          <Typography variant='h3' sx={{ mt: '18px', mb: '32px' }}>
            Mes coordonnées
          </Typography>
          <EditButtons sx={{ position: 'absolute', top: isMobile ? '12px' : '48px', right: '0' }} />
          
          {/* Error Messages Section */}
          {errorMessages.length > 0 && (
            <Box ref={errorsRef} sx={{ mb: '24px' }}>
              <Alert severity="error">
                <AlertTitle>Veuillez corriger les erreurs suivantes :</AlertTitle>
                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
                  {errorMessages.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </Box>
              </Alert>
            </Box>
          )}
          
          <Stack spacing='34px' sx={{ mb: '30px' }}>
            <IdentityForm formik={formik} listTitles={listTitles || []} />
            <MainPhoneForm formik={formik} />
            <OtherPhonesForm formik={formik} />
            <AdressForm formik={formik} />
            <AdditionalInformationForm formik={formik} />
          </Stack>
          <EditButtons sx={{ float: 'right' }} />
        </Box>
      </FormikContext.Provider>
    </EditableContext.Provider>
  )
}

export default PersonalInformationsForm
